import { graphql } from '@/gql'
import type { DocumentNode } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { Text } from '../base/text'
import type { ConnectorFamilyPinRole } from '@/gql/graphql'
import { Badge, type BadgeProps } from '../base/badge'
import { useCallback } from 'react'
import { BounceLoader } from 'react-spinners'
import clsx from 'clsx'

const CreateItemTypeConnectorFamilyMutation = graphql(`
  mutation CreateItemTypeConnectorFamily($input: CreateItemTypeConnectorFamilyInput!) {
    createItemTypeConnectorFamily(input: $input) {
      itemTypeConnectorFamily { id itemType { id } connectorFamily { id } }
      errors { path message }
    }
  }
`)

const DestroyItemTypeConnectorFamilyMutation = graphql(`
  mutation DestroyItemTypeConnectorFamily($input: DestroyItemTypeConnectorFamilyInput!) {
    destroyItemTypeConnectorFamily(input: $input) {
      itemTypeConnectorFamily { id }
      errors { path message }
    }
  }
`)

const pinRoleColors: { [role: string]: BadgeProps['color'] } = {
  earth: 'green',
  live: 'orange',
  neutral: 'blue',
  shield: 'yellow',
  signal: 'red',
}

interface Props {
  itemTypeId: string
  itemTypeConnectorFamilyId?: string
  connectorFamily: {
    id: string
    name: string
    connectors: {
      count: number
    }
    connectorFamilyPins: { id: string, name: string, role: ConnectorFamilyPinRole }[]
  }
  refetchQueries?: DocumentNode[]
}

export const Toggle = ({ itemTypeId, connectorFamily, itemTypeConnectorFamilyId, refetchQueries }: Props) => {
  const [create, { loading: createLoading }] = useMutation(CreateItemTypeConnectorFamilyMutation, { refetchQueries })
  const [destroy, { loading: destroyLoading }] = useMutation(DestroyItemTypeConnectorFamilyMutation, { refetchQueries })

  const onClick = useCallback(() => {
    if (itemTypeConnectorFamilyId === undefined) {
      create({ variables: { input: { itemTypeId, connectorFamilyId: connectorFamily.id } } })
    } else {
      destroy({ variables: { input: { id: itemTypeConnectorFamilyId } } })
    }
  }, [connectorFamily, create, destroy, itemTypeConnectorFamilyId, itemTypeId])

  return (
    <div className={ clsx('flex flex-col gap-3 p-6 border-2 rounded-xl items-center', createLoading || destroyLoading ? 'border-amber-500' : itemTypeConnectorFamilyId === undefined ? 'border-red-500' : 'border-green-500') } onClick={ onClick }>
      <Text className="font-semibold" size="xl">{ connectorFamily.name }</Text>
      <Text size="lg">{ connectorFamily.connectors.count } connectors</Text>
      <div className="flex flex-row flex-wrap gap-3 items-center">
        { connectorFamily.connectorFamilyPins.map(cfp => <Badge color={ pinRoleColors[cfp.role] } key={ cfp.id }>{ cfp.name }</Badge>) }
      </div>
      { (createLoading || destroyLoading) && <BounceLoader color="lightgray" size={ 16 } /> }
    </div>
  )
}
